import {z} from 'zod';

import type {Json} from '../../../HeliportJS/dist/models/json';
export const rtcConfigSchema = z.object({
  position: z.object({}),
  voice: z.object({}),
  services: z.object({
    carnelian: z
      .object({
        keepaliveInterval: z.number().nonnegative(),
        interestRadius: z.number().nonnegative(),
        attractRadius: z.number().nonnegative(),
        roleLimit: z.number().nonnegative()
      })
      .optional(),
    skyway: z.object({}).optional(),
    agora: z.object({}).optional(),
    smcn: z
      .object({
        minDistance: z.number().nonnegative(),
        maxDistance: z.number().nonnegative(),
        rollOffFactor: z.number().nonnegative(),
        maxAttenuateDiff: z.number().nonnegative()
      })
      .optional()
  })
});

type NotifyDataBase = {
  ActionType: string;
  CreatedAt: {
    seconds: number;
    nanoseconds: number;
  };
};

type BaseAvatarInfoDocument = NotifyDataBase & {
  ActionType: 'AvatarInfo';
  ChannelSessionCode: string;
};
type SetAvatarInfoDocument = BaseAvatarInfoDocument & {
  AvatarInfo: {
    UUID: string;
    TargetAge: 'all_ages' | 'r18';
    PolygonCount: number;
    HrmPNG: string;
    HrmASTC: string | null;
    HrmETC2: string | null;
  };
};
type UnsetAvatarInfoDocument = BaseAvatarInfoDocument & {};
export type AvatarInfoDocument = SetAvatarInfoDocument | UnsetAvatarInfoDocument;

export type BaseAvatarInfo = {
  uuid: string;
  target_age: string;
  polygon_count: number;
  hrm_png: string; // http://example.com
  hrm_astc: string | null; // http://example.com
  hrm_etc2: string | null; // http://example.com
};

export type AvatarInfo = BaseAvatarInfo & {
  id?: number;
  channel_session_code?: string;
  token: string;
  vket_id?: string;
  thumbnail: string;
  motion_type: 'general';
  avatar_type: string;
  vrm_version: string;
  created_at: string;
  updated_at: string;
};

export type ChannelSessionDetail = {
  id: number;
  channel_session_code: string;
  user_code: string;
  world_code: string | null;
  position_channel_code: string | null;
  voice_channel_code: string | null;
  live_channel_code: string | null;
  user_role: string | null;
  created_at: string;
  updated_at: string;
  user_type: 'guest' | 'login';
  avatar_info?: AvatarInfo;
};

/** @see https://swagger-ui.cdn.v-market.work/VketCloudRDBChannelSessionServer/schemas/User */
export type NetworkUser = {
  id?: number;
  uid: string;
  icon: string;
  avatar: string | null;
  name: string;
  user_type: 'guest' | 'login';
  expire_at?: string;
  created_at?: string;
  updated_at?: string;
};

/**
 * @see https://swagger-ui.cdn.v-market.work/VketCloudRDBChannelSessionServer/schemas/PositionChannel
 * @see https://github.com/VRHIKKY/Channel-Session-Manager/blob/develop/db/schemas/100_position_channels.schema
 */
export type PositionChannelDetail = {
  id?: number;
  channel_id: string;
  spatium_code: string;
  world_code: string;
  channel_type: string;
  create_user_code: string | null;
  name: string | null;
  description: string | null;
  channel_password?: string;
  max_player_count: number;
  max_voice_player_count: number;
  allow_multi_voice_channel: boolean;
  enable_default_voice_channel: boolean;
  required_tickets?: boolean;
  heartbeat_deactive_sec?: number;
  allow_set_open_at: string;
  allow_set_expire_at: string;
  allow_set_ban: string;
  require_login?: boolean;
  is_prism: boolean;
  open_at: string | null;
  expire_at: string | null;
  keepUntil: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_heartbeat: number;
  users: NetworkUser[];
  channel_sessions: Array<ChannelSessionDetail>;
  position_rtc_type: 'skyway' | 'carnelian' | 'carnelian-prism';
  voice_rtc_type: 'skyway' | 'skyway2' | 'agora' | 'smcn';
  rtc_config: Json;
};

export type VoiceChannelDetail = {
  channel_id: string;
  position_channel_id: string;
  channel_type: string;
  create_user_code: string;
  name: string;
  description: string;
  max_player_count: number; // API未実装だが必要
  users: [];
  channel_sessions: Array<ChannelSessionDetail>;
  is_global: boolean;
  rtc_config: Json;
};

export type LiveChannelDetail = {
  channel_id: string;
  spatium_code: string;
  world_code: string;
  position_channel_code: string;
  name: string;
  description: string;
  max_presenter_count: number; // API未実装だが必要
  required_tickets: boolean;
  require_login: boolean;
  users: [];
  channel_sessions: Array<ChannelSessionDetail>;
};

/**
 * @see https://swagger-ui.cdn.v-market.work/VketCloudRDBChannelSessionServer/schemas/authorization
 * @see https://discord.com/channels/596169759045976094/1071027694524956722/1328616059774828556
 */
type Authorization = {
  user_code: string;
  role?: string;
  permission?: string;
  force_entry?: boolean;
  attribute?: string;
  position_channel_code?: string;
  world_code?: string;
  spatium_code?: string;
  exp?: string;
};

/** @see https://swagger-ui.cdn.v-market.work/VketCloudRDBChannelSessionServer/operations/put-channel_session-position_channel */
export type EnterPositionChannelData = {
  position_channel: PositionChannelDetail;
  authorization: Authorization;
};

/** @see https://github.com/VRHIKKY/Channel-Session-Manager/blob/0c853f6997aac90d881b3c799e74ab0f883ad2f6/app/controllers/channel_session/voice_channels_controller.rb#L77 */
export type EnterVoiceChannelData = {
  voice_channel: VoiceChannelDetail;
};

export type RequestObjectOwnershipData = {
  objectId: string;
  channelSessionCode: string;
  error?:
    | {
        message: string;
        code: string;
      }
    | undefined;
}[];

/**
 * @remarks Channel Session JWTのパース結果。
 * @see https://swagger-ui.cdn.v-market.work/VketCloudRDBChannelSessionServer/schemas/ChannelSession
 * @see https://discord.com/channels/596169759045976094/1243065534916853930/1343759407304937525
 */
export type ChannelSession = {
  /** @example 'O1zPwLn9KQrfZXV8' */
  channel_session_code: string;
  /**
   * @example 'qs1DQsh56aUw3OFl'
   * @see https://discord.com/channels/596169759045976094/1326808604581298309/1334852789012267079
   */
  user_code: string;
  /** @example 'my_vket_id' */
  world_code?: string | null;
  /** @example '4mdTRQXrODxnjDoa' */
  position_channel_code: string;
  /** @example '9gpdWD0s6wvdkVg6' */
  voice_channel_code?: string | null;
  /** @example '9gpdWD0s6wvdkVg6' */
  live_channel_code?: string | null;
  user_role?: string | null;
  /** @type date-time */
  created_at?: string;
  /** @type date-time */
  updated_at?: string;
  user_type?: 'guest' | 'login';
};

/** @see https://swagger-ui.cdn.v-market.work/VketCloudRDBChannelSessionServer/operations/put-channel_session */
export type RefreshChannelSessionData = {
  channel_session: ChannelSession;
  /**
   * @remarks
   * unix timestamp
   * @type integer
   */
  response_timestamp: number;
};
